<template>
  <div
    ref="calendarRoot"
    :class="['absolute z-100 bg-white shadow-lg rounded-lg mt-2 max-w-full sm:max-w-sm md:max-w-md lg:max-w-lg overflow-hidden', positionClass]">
    <!-- Navigasi Bulan -->
    <div class="flex justify-between items-center mb-4 p-4">
      <button @click="prevMonth" class="text-gray-500 rounded bg-gray-50">
        <AppIcon icon="material-symbols:navigate-before" class="w-7 h-7" />
      </button>
      <div class="flex gap-2">
        <!-- data-testid = kontrak regresi: navigasi ke bulan/tahun target (0-indexed month). -->
        <select data-testid="datepicker-month" v-model="selectedMonth" @change="updateMonth" class="text-gray-800 rounded bg-gray-50">
          <option v-for="(month, index) in months" :key="index" :value="index">{{ month }}</option>
        </select>
        <select data-testid="datepicker-year" v-model="selectedYear" @change="updateYear" class="text-gray-800 rounded bg-gray-50">
          <option v-for="year in yearRange" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>
      <button @click="nextMonth" class="text-gray-500 rounded bg-gray-50">
        <AppIcon icon="material-symbols:navigate-next" class="w-7 h-7" />
      </button>
    </div>

    <!-- Grid Kalender -->
    <div class="grid grid-cols-7 gap-1 sm:gap-2 px-2 sm:px-4">
      <!-- Nama Hari -->
      <div v-for="day in dayNames" :key="day" class="text-gray-600 text-xs text-center font-medium">
        {{ day }}
      </div>
      <!-- Tanggal -->
      <!-- data-testid + data-date = kontrak regresi: satu-satunya cara memilih tanggal secara
           deterministik tanpa menghitung posisi sel. Jangan dihapus. -->
      <div v-for="day in currentMonthDays" :key="day ? day.date : 'empty'" :class="getDayCellClass(day)"
        data-testid="day-cell"
        :data-date="dayKey(day)"
        :data-state="day ? getDateStatus(day.date) : 'empty'"
        @click="handleDayClick(day, $event)"
        @mouseenter="handleDayMouseEnter(day, $event)"
        @mouseleave="handleDayMouseLeave(day)"
        class="relative flex flex-col items-center justify-center rounded p-1 cursor-pointer w-9 h-9 sm:w-10 sm:h-10">
        <span v-if="day" class="text-xs sm:text-sm">{{ day.date.getDate() }}</span>
        <span
          v-if="day && getDateStatus(day.date) === 'outside-window'"
          class="absolute -bottom-1 -right-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-blue-200 bg-blue-50"
        >
          <AppIcon icon="mdi:calendar-plus" class="h-2.5 w-2.5 text-blue-700" />
        </span>
        <span
          v-if="day && isToday(day.date)"
          :class="[
            'text-[8px] sm:text-[9px] leading-none mt-0.5',
            isSelected(day.date) ? 'text-white/90' : 'text-blue-600'
          ]"
        >
          Hari ini
        </span>
        <span v-if="day && !isDateUnavailable(day.date) && showPrice" class="text-xs text-green-500 mt-1">
          {{ day.price ? day.price : '' }}
        </span>
      </div>
    </div>

    <div v-if="currentMonthHolidayNotes.length" class="px-4 pb-2 pt-3">
      <p
        v-for="note in currentMonthHolidayNotes"
        :key="note.key"
        :class="[
          'text-[11px]',
          note.typeId === 2 ? 'text-yellow-700' : 'text-red-600',
        ]"
      >
        {{ note.label }}
      </p>
    </div>

    <transition name="fade">
      <div
        v-if="activeTooltip && activeTooltip.title"
        class="pointer-events-none absolute z-[120] w-52 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-xl"
        :style="tooltipStyle"
      >
        <p class="text-xs font-semibold text-gray-800">{{ activeTooltip.title }}</p>
        <p v-if="activeTooltip.message" class="mt-1 text-xs text-gray-700">{{ activeTooltip.message }}</p>
      </div>
    </transition>

    <!-- Tombol Clear dan Apply -->
    <div class="flex justify-end mt-4 p-4 w-100">
      <button @click="clearDate" class="text-sm text-red-500">Clear date</button>
      <!-- <button @click="applyDate" class="bg-blue-500 text-white px-4 py-2 rounded-full">
          Apply
        </button> -->
    </div>
  </div>
</template>

<script>
import {
  format,
  addDays,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval
} from 'date-fns';

export default {
  name: 'BaseDate',
  props: {
    selectedDate: {
      type: [Date, Object],
      default: null
    },
    position: {
      type: String,
      default: 'right', // posisi default
      validator: value => ['left', 'right'].includes(value)
    },
    range: {
      type: Boolean,
      default: false
    },
    checkInDate: {
      type: Date,
      default: null
    },
    focusMonth: {
      type: Date,
      default: null
    },
    disablePastDates: {
      type: Boolean,
      default: true, // Secara default, tanggal sebelumnya dinonaktifkan
    },
    showPrice: {
      type: Boolean,
      default: true,
    },
    roomId: {
      type: String,
      default: null,
    },
    roomTypeId: {
      type: String,
      default: null,
    },
    propertyId: {
      type: [String, Number],
      default: null,
    },
    applyAvailabilityWindow: {
      type: Boolean,
      default: false,
    },
    availabilityWindowDays: {
      type: Number,
      default: null,
    },
    approvedBookingRequestDate: {
      type: [Date, String, Number, null],
      default: null,
    },
    // Rentang tanggal milik order yang sedang di-reschedule. Tanggal di dalam rentang ini
    // tidak di-disable (self-conflict) karena okupansi booking sendiri dikecualikan saat
    // revalidasi di backend.
    excludeStart: {
      type: [Date, String, Number, null],
      default: null,
    },
    excludeEnd: {
      type: [Date, String, Number, null],
      default: null,
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    const initialDate = this.range ? (this.selectedDate ? this.selectedDate.startDate : new Date()) : (this.selectedDate || new Date());
    const maxDate = new Date(initialDate); // Tetapkan maxDate
    maxDate.setFullYear(maxDate.getFullYear() + 1); // Tambah 1 tahun dari initialDate

    return {
      currentMonth: new Date(initialDate),
      maxDate, // Tambahkan maxDate ke data
      internalSelectedDate: this.selectedDate,
      dayNames: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      selectedMonth: new Date(initialDate).getMonth(),
      selectedYear: new Date(initialDate).getFullYear(),
      yearRange: Array.from({ length: currentYear - 1970 + 2 }, (_, i) => 1970 + i), //Dapat tambahkankan tahun sekarang Default +2 Tahun ini dan 1 tahun kedepan
      prices: {
        '2024-12-30': '599rb',
        '2024-12-28': '299rb',
        '2025-01-01': '321rb',
        '2025-01-02': '150rb',
        '2025-01-03': '210rb',
        '2025-01-04': '390rb',
        '2025-01-05': '275rb',
        '2025-01-06': '310rb',
        '2025-01-07': '180rb',
        '2025-01-08': '230rb',
        '2025-01-09': '400rb',
        '2025-01-10': '100rb',
        '2025-01-11': '350rb',
        '2025-01-12': '290rb',
        '2025-01-13': '185rb',
        '2025-01-14': '370rb',
        '2025-01-15': '220rb',
        '2025-01-16': '320rb',
        '2025-01-17': '275rb',
        '2025-01-18': '190rb',
        '2025-01-19': '155rb',
        '2025-01-20': '399rb',
        '2025-01-21': '105rb',
        '2025-01-22': '280rb',
        '2025-01-23': '340rb',
        '2025-01-24': '200rb',
        '2025-01-25': '310rb',
        '2025-01-26': '160rb',
        '2025-01-27': '385rb',
        '2025-01-28': '140rb',
        '2025-01-29': '240rb',
        '2025-01-30': '350rb',
        '2025-01-31': '180rb',
      },
      selectingRange: false,
      bookedDates: [],
      disabledDateReasons: {},
      outsideWindowDateReasons: {},
      apiAvailabilityWindowDays: null,
      // CH-W40 - floor check-in paling awal dari backend (H-1 bila toggle malam kemarin aktif). null = default hari ini.
      earliestBookableDate: null,
      roomIdState: this.roomId,
      roomTypeIdState: this.roomTypeId,
      propertyIdState: this.propertyId,
      activeTooltip: null,
      tooltipStyle: { top: "0px", left: "0px" },
      holidayDateMap: {
        "2026-03-20": { name: "Hari Raya Idul Fitri", type: "Libur Nasional", typeId: 1 },
        "2026-03-21": { name: "Hari Raya Idul Fitri", type: "Libur Nasional", typeId: 1 },
      },
    };
  },
  computed: {
    resolvedAvailabilityWindowDays() {
      if (Number.isInteger(this.apiAvailabilityWindowDays)) {
        return this.apiAvailabilityWindowDays;
      }
      if (Number.isInteger(this.availabilityWindowDays)) {
        return this.availabilityWindowDays;
      }
      return null;
    },
    currentMonthYear() {
      return format(this.currentMonth, 'MMMM yyyy');
    },
    effectiveMaxDate() {
      if (
        this.applyAvailabilityWindow &&
        Number.isInteger(this.resolvedAvailabilityWindowDays)
      ) {
        if (this.resolvedAvailabilityWindowDays <= 0) {
          return this.todayAtMidnight();
        }

        return addDays(this.todayAtMidnight(), this.resolvedAvailabilityWindowDays - 1);
      }

      return this.maxDate;
    },
    currentMonthDays() {
      const start = startOfMonth(this.currentMonth);
      const end = endOfMonth(this.currentMonth);

      // Dapatkan indeks hari pertama bulan (0 = Minggu, 1 = Senin, dst.)
      const firstDayIndex = start.getDay();

      // Buat placeholder untuk hari kosong sebelum hari pertama
      const emptyDays = Array.from({ length: firstDayIndex }, () => null);

      // Buat array untuk semua tanggal bulan ini
      const daysInMonth = eachDayOfInterval({ start, end }).map(date => ({
        date,
        price: this.prices[format(date, 'yyyy-MM-dd')] || null,
      }));

      // Gabungkan hari kosong dengan hari-hari di bulan
      return [...emptyDays, ...daysInMonth];
    },
    positionClass() {
      return this.position === 'right' ? 'sm:left-auto sm:right-0' : 'sm:left-0 sm:right-auto';
    },
    currentMonthHolidayNotes() {
      const notes = [];
      const currentYear = this.currentMonth.getFullYear();
      const currentMonth = this.currentMonth.getMonth();

      Object.entries(this.holidayDateMap).forEach(([dateKey, item]) => {
        const dateObj = this.toDateOnly(dateKey);
        if (!dateObj) return;
        if (dateObj.getFullYear() !== currentYear || dateObj.getMonth() !== currentMonth) return;
        notes.push({
          key: `${dateKey}-${item?.typeId || 1}`,
          day: dateObj.getDate(),
          typeId: Number(item?.typeId) === 2 ? 2 : 1,
          label: `${dateObj.getDate()} ${item?.name || ""}`.trim(),
        });
      });

      return notes.sort((a, b) => Number(a.day) - Number(b.day));
    }
  },
  methods: {
    todayAtMidnight() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    isAvailabilityWindowDisabled(date) {
      if (
        !this.applyAvailabilityWindow ||
        !Number.isInteger(this.resolvedAvailabilityWindowDays)
      ) {
        return false;
      }

      if (this.resolvedAvailabilityWindowDays <= 0) {
        return true;
      }

      return date > this.effectiveMaxDate;
    },
    isWithinExcludeRange(date) {
      const start = this.toDateOnly(this.excludeStart);
      const end = this.toDateOnly(this.excludeEnd);
      const target = this.toDateOnly(date);
      if (!start || !end || !target) return false;
      // Inklusif kedua ujung agar seluruh rentang order sendiri tetap bisa dipilih.
      return target.getTime() >= start.getTime() && target.getTime() <= end.getTime();
    },
    isDateDisabled(date) {
      if (this.isPastDate(date)) return true;
      // Tanggal milik order sendiri tidak di-disable (self-conflict dikecualikan di backend).
      if (this.isWithinExcludeRange(date)) return false;
      const reasonCode = this.getDisabledReasonCode(date);
      return !!reasonCode && reasonCode !== "OUTSIDE_WINDOW";
    },
    isDateUnavailable(date) {
      return this.isDateDisabled(date) || this.isAvailabilityWindowDisabled(date);
    },
    isApprovedBookingRequestDate(date) {
      const approvedDate = this.toDateOnly(this.approvedBookingRequestDate);
      const targetDate = this.toDateOnly(date);

      if (!approvedDate || !targetDate) return false;
      return approvedDate.getTime() === targetDate.getTime();
    },
    getDateStatus(date) {
      if (!date) return "empty";
      if (this.isDateDisabled(date)) return "unavailable";
      if (this.isApprovedBookingRequestDate(date)) return "approved";
      if (this.isHolidayDate(date)) {
        return this.getHolidayTypeId(date) === 2 ? "holiday-collective" : "holiday-national";
      }
      if (this.isOutsideWindowDate(date) || this.isAvailabilityWindowDisabled(date)) return "outside-window";
      return "available";
    },
    isOutsideWindowDate(date) {
      const dateKey = this.toLocalDateString(this.toDateOnly(date));
      if (!dateKey) return false;
      return this.outsideWindowDateReasons[dateKey] === "OUTSIDE_WINDOW";
    },
    getDayCellClass(day) {
      if (!day || !day.date) return {};

      const date = day.date;
      const status = this.getDateStatus(date);

      return {
        "bg-blue-500 text-white": this.isSelected(date),
        "bg-blue-50": this.isInRange(date),
        "cursor-not-allowed": this.isDateDisabled(date),
        "border border-gray-300 bg-gray-100 text-gray-500": !this.isSelected(date) && status === "unavailable",
        "border border-green-200 bg-green-50 text-green-700 font-semibold": !this.isSelected(date) && status === "approved",
        "border border-red-200 bg-red-50 text-red-700": !this.isSelected(date) && status === "holiday-national",
        "border border-yellow-300 bg-yellow-50 text-yellow-700": !this.isSelected(date) && status === "holiday-collective",
        "border border-gray-300 bg-white text-gray-900": !this.isSelected(date) && (status === "available" || status === "outside-window"),
      };
    },
    // Kunci tanggal untuk data-date (kontrak regresi). Options API tidak bisa memanggil
    // import modul langsung dari template, jadi dibungkus method.
    dayKey(day) {
      return day ? format(day.date, "yyyy-MM-dd") : null;
    },
    isToday(date) {
      const dateOnly = this.toDateOnly(date);
      const today = this.todayAtMidnight();
      if (!dateOnly || !today) return false;
      return dateOnly.getTime() === today.getTime();
    },
    isHolidayDate(date) {
      const dateKey = this.toLocalDateString(this.toDateOnly(date));
      if (!dateKey) return false;
      return Boolean(this.holidayDateMap[dateKey]?.name);
    },
    getHolidayTypeId(date) {
      const dateKey = this.toLocalDateString(this.toDateOnly(date));
      if (!dateKey) return 1;
      const rawTypeId = this.holidayDateMap[dateKey]?.typeId;
      return Number(rawTypeId) === 2 ? 2 : 1;
    },
    getDisabledReason(date) {
      const reasonCode = this.getDisabledReasonCode(date);
      return this.mapReasonMessage(reasonCode);
    },
    getDisabledReasonCode(date) {
      const dateKey = this.toLocalDateString(this.toDateOnly(date));
      if (!dateKey) return null;
      return this.disabledDateReasons[dateKey] || null;
    },
    mapReasonMessage(reasonCode) {
      if (!reasonCode) return null;

      const reasonMap = {
        OUTSIDE_WINDOW: "Di luar availability window",
        OUT_OF_SERVICE: "Tanggal ditutup operasional",
        BOOKING_CONFLICT: "Sudah terisi booking",
      };

      return reasonMap[reasonCode] || null;
    },
    clampCurrentMonth() {
      const selectedAnchorDate = this.getSelectedAnchorDate();
      if (selectedAnchorDate) {
        const selectedMonth = new Date(
          selectedAnchorDate.getFullYear(),
          selectedAnchorDate.getMonth(),
          1
        );
        this.currentMonth = selectedMonth;
        this.updateSelectedMonthYear();
        return;
      }

      const latestAllowedMonth = new Date(
        this.effectiveMaxDate.getFullYear(),
        this.effectiveMaxDate.getMonth(),
        1
      );

      if (this.currentMonth > latestAllowedMonth) {
        this.currentMonth = latestAllowedMonth;
        this.updateSelectedMonthYear();
      }
    },
    updateSelectedMonthYear() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      this.selectedYear = year;
      this.selectedMonth = month;
    },
    prevMonth() {
      this.currentMonth = subMonths(this.currentMonth, 1);
      this.updateSelectedMonthYear();
    },
    nextMonth() {
      this.currentMonth = addMonths(this.currentMonth, 1);
      this.updateSelectedMonthYear();
    },
    updateMonth() {
    const tentativeMonth = new Date(this.selectedYear, this.selectedMonth, 1);

    if (tentativeMonth > this.effectiveMaxDate) {
      // Jika tanggal melebihi batas, kembalikan ke maxDate
      this.currentMonth = new Date(this.effectiveMaxDate.getFullYear(), this.effectiveMaxDate.getMonth(), 1);
      this.selectedMonth = this.effectiveMaxDate.getMonth();
      this.selectedYear = this.effectiveMaxDate.getFullYear();
    } else {
      this.currentMonth = tentativeMonth;
    }
  },
  updateYear() {
    const tentativeYear = new Date(this.selectedYear, this.selectedMonth, 1);

    if (tentativeYear > this.effectiveMaxDate) {
      // Jika tahun melebihi batas, kembalikan ke maxDate
      this.currentMonth = new Date(this.effectiveMaxDate.getFullYear(), this.effectiveMaxDate.getMonth(), 1);
      this.selectedMonth = this.effectiveMaxDate.getMonth();
      this.selectedYear = this.effectiveMaxDate.getFullYear();
    } else {
      this.currentMonth = tentativeYear;
    }
  },
    setCurrentMonth(date) {
      if (date instanceof Date && !isNaN(date)) {
        const year = date.getFullYear();
        const month = date.getMonth();
        // Buat date di awal bulan untuk menghindari masalah timezone
        const newMonth = new Date(year, month, 1, 12, 0, 0);
        this.currentMonth = newMonth;
        this.selectedMonth = month;
        this.selectedYear = year;

        // console.log("setCurrentMonth Updated:", this.currentMonth);
      }
    },
    selectDate(date) {
      let normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);

      if (this.range) {
        if (!this.selectingRange || !this.internalSelectedDate.startDate) {
          this.internalSelectedDate = { startDate: normalizedDate, endDate: null };
          this.selectingRange = true;
        } else {
          if (normalizedDate < this.internalSelectedDate.startDate) {
            this.internalSelectedDate = { startDate: normalizedDate, endDate: this.internalSelectedDate.startDate };
          } else {
            this.internalSelectedDate.endDate = normalizedDate;
          }
          this.selectingRange = false;
          this.applyDate();
        }
      } else {
        this.internalSelectedDate = normalizedDate;
        this.currentMonth = new Date(normalizedDate);
        this.applyDate();
      }
    },
    isSelected(date) {
      if (!date || !this.internalSelectedDate) return false;

      const start = this.internalSelectedDate.startDate ? new Date(this.internalSelectedDate.startDate) : null;
      const end = this.internalSelectedDate.endDate ? new Date(this.internalSelectedDate.endDate) : null;
      if (start) start.setHours(0, 0, 0, 0);
      if (end) end.setHours(0, 0, 0, 0);

      if (this.range) {
        if (start && end) {
          return date.getTime() === start.getTime() || date.getTime() === end.getTime();
        } else if (start) {
          return date.getTime() === start.getTime();
        }
      } else {
        return this.internalSelectedDate && date.toDateString() === this.internalSelectedDate.toDateString();
      }
      return false;
    },
    isInRange(date) {
      if (this.range && this.internalSelectedDate && this.internalSelectedDate.startDate && this.internalSelectedDate.endDate) {
        const start = new Date(this.internalSelectedDate.startDate);
        const end = new Date(this.internalSelectedDate.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        return date > start && date < end;
      }
      return false;
    },
    // isPastDate(date) {
    //   if (!this.disablePastDates) {
    //     return false; // Jika disablePastDates false, semua tanggal aktif
    //   }
    //   const today = new Date();
    //   today.setHours(0, 0, 0, 0); // Hanya tanggal tanpa waktu
    //   if (this.checkInDate) {
    //     return date < this.checkInDate; // Jika ada checkInDate, gunakan itu
    //   }
    //   return date < today; // Jika tidak, bandingkan dengan hari ini
    // },
    isPastDate(date) {
      if (!this.disablePastDates) {
        return false; // Jika disablePastDates false, semua tanggal aktif
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Hanya tanggal tanpa waktu

      // Periksa apakah checkInDate ada
      if (this.checkInDate) {
        const checkInDateOnly = new Date(this.checkInDate);
        checkInDateOnly.setHours(0, 0, 0, 0); // Hanya tanggal tanpa waktu

        // Nonaktifkan tanggal sebelum atau sama dengan checkInDate
        return date <= checkInDateOnly;
      }

      // CH-W40 - floor bisa mundur ke H-1 bila backend mengizinkan booking "malam kemarin".
      let floor = today;
      if (this.earliestBookableDate) {
        const parts = String(this.earliestBookableDate).split("-").map(Number);
        if (parts.length === 3 && !parts.some(Number.isNaN)) {
          const e = new Date(parts[0], parts[1] - 1, parts[2]); // local midnight, tak geser TZ
          if (e < floor) floor = e;
        }
      }
      // Default: Nonaktifkan tanggal sebelum floor
      return date < floor;
    },
    clearDate() {
      this.internalSelectedDate = this.range ? { startDate: null, endDate: null } : null;
      this.selectingRange = false;
      this.hideTooltip();
    },
    applyDate() {
      this.$emit('dateSelected', this.internalSelectedDate);
      this.$emit('close');
    },
    closeModal() {
      this.$emit('close');
    },
    toLocalDateString(dateObj) {
      // Pastikan dateObj adalah Date valid
      if (!(dateObj instanceof Date) || isNaN(dateObj)) {
        return null; // atau kembalikan string kosong ""
      }

      const year = dateObj.getFullYear();
      // getMonth() dimulai dari 0, jadi tambahkan +1
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');

      // Return format YYYY-MM-DD
      return `${year}-${month}-${day}`;
    },
    toDateOnly(value) {
      if (!value) return null;
      const date = value instanceof Date ? new Date(value) : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date;
    },
    getSelectedAnchorDate() {
      if (this.range) {
        const startDate = this.internalSelectedDate?.startDate;
        return this.toDateOnly(startDate);
      }
      return this.toDateOnly(this.internalSelectedDate);
    },
    async getRoomOccupancy() {

      const current = new Date(this.currentMonth);

      const startDate = new Date(current.getFullYear(), current.getMonth(), 1);
      const endDate = new Date(current.getFullYear(), current.getMonth() + 1, 0);

      const startLocal = this.toLocalDateString(new Date(startDate));
      const endLocal = this.toLocalDateString(new Date(endDate));

      console.log('roomIdState', this.roomIdState)
      console.log('roomTypeIdState', this.roomTypeIdState)

      if (!this.roomIdState && !this.roomTypeIdState) {
        return;
      }

      try {
        const parsedPropertyId = Number(this.propertyIdState);
        const resolvedPropertyId =
          Number.isInteger(parsedPropertyId) && parsedPropertyId > 0
            ? parsedPropertyId
            : null;

        const payload = {
          fromDate: startLocal,
          toDate: endLocal,
          roomId: !this.roomIdState ? null : this.roomIdState,
          roomTypeId: !this.roomTypeIdState ? null : this.roomTypeIdState,
          propertyId: resolvedPropertyId,
        };

        const response = await this.$store.dispatch("properties/getOccupancyDate", payload);
        const apiWindowDaysRaw =
          response?.AvailabilityWindow?.WindowDays
          ?? response?.availabilityWindow?.WindowDays
          ?? response?.data3?.WindowDays
          ?? null;
        const apiWindowDays = Number.isInteger(apiWindowDaysRaw)
          ? apiWindowDaysRaw
          : parseInt(apiWindowDaysRaw, 10);
        this.apiAvailabilityWindowDays = Number.isNaN(apiWindowDays) ? null : apiWindowDays;
        // CH-W40 - floor H-1 dari backend (bila toggle malam kemarin aktif & belum lewat jam check-out).
        this.earliestBookableDate = response?.EarliestBookableDate || response?.earliestBookableDate || null;

        const disabledDates = {};
        const outsideWindowDates = {};
        const bookedDates = [];
        const holidayMap = {};

        for (const item of response.data || []) {
          if (typeof item === "string") {
            disabledDates[item] = "BOOKING_CONFLICT";
            bookedDates.push(item);
            continue;
          }

          const dateValue = item?.date || item?.Date || item?.day || item?.Day;
          const dateKey = this.toLocalDateString(new Date(dateValue));
          if (!dateKey) continue;

          const reasonCode =
            item?.reason ||
            item?.Reason ||
            item?.code ||
            item?.Code ||
            "BOOKING_CONFLICT";

          if (reasonCode === "OUTSIDE_WINDOW") {
            outsideWindowDates[dateKey] = "OUTSIDE_WINDOW";
            continue;
          }

          disabledDates[dateKey] = reasonCode;

          if (reasonCode === "BOOKING_CONFLICT") {
            bookedDates.push(dateKey);
          }
        }

        for (const holiday of response?.HolidayOverlay || response?.holidayOverlay || []) {
          const dateValue = holiday?.date || holiday?.Date || holiday?.day || holiday?.Day;
          const dateKey = this.toLocalDateString(new Date(dateValue));
          if (!dateKey) continue;

          const name = String(holiday?.name || holiday?.Name || "").trim();
          if (!name) continue;
          const rawType =
            holiday?.TypeId ??
            holiday?.typeId ??
            holiday?.Type ??
            holiday?.type;
          const rawTypeIdNumeric = Number(rawType);
          const rawTypeLabel = String(rawType || "").toLowerCase();
          const holidayNameLabel = String(name || "").toLowerCase();
          const inferredTypeId = Number.isNaN(rawTypeIdNumeric)
            ? ((rawTypeLabel.includes("cuti") || holidayNameLabel.includes("cuti bersama")) ? 2 : 1)
            : (rawTypeIdNumeric === 2 ? 2 : 1);
          const current = holidayMap[dateKey];
          // If a date has multiple holiday events, prioritize "Cuti Bersama" (yellow)
          // so it doesn't get overwritten by a later "Libur Nasional" row.
          const finalTypeId =
            current?.typeId === 2 || inferredTypeId === 2
              ? 2
              : 1;
          holidayMap[dateKey] = {
            name: current?.name || name,
            typeId: finalTypeId,
            type: finalTypeId === 2 ? "Cuti Bersama" : "Libur Nasional",
          };
        }

        this.disabledDateReasons = disabledDates;
        this.outsideWindowDateReasons = outsideWindowDates;
        this.bookedDates = bookedDates;
        this.holidayDateMap = holidayMap;
        console.log("getRoomOccupancy fetched:", response);  
      } catch (error) {
        console.error("Error fetching getRoomOccupancy:", error);
      }
    },
    isBooked(date) {
      return this.getDisabledReason(date) === "Sudah terisi booking";
    },
    buildTooltipPayload(date, target) {
      if (!date || !target) return null;

      let title = "";
      let message = null;

      if (this.isApprovedBookingRequestDate(date)) {
        title = "Tanggal approved booking request";
        message = "Tanggal check-in yang disetujui dari booking request.";
      } else if (this.isAvailabilityWindowDisabled(date)) {
        return null;
      } else {
        title = "Tanggal tidak tersedia";
        message = null;
      }

      if (!title) return null;

      const root = this.$refs.calendarRoot;
      if (!root) return { key: this.toLocalDateString(date), title, message };

      const rootRect = root.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const tooltipWidth = 208;
      const top = targetRect.top - rootRect.top + targetRect.height + 8;
      const centeredLeft =
        targetRect.left -
        rootRect.left +
        targetRect.width / 2 -
        tooltipWidth / 2;
      const maxLeft = Math.max(8, rootRect.width - tooltipWidth - 8);
      const left = Math.min(Math.max(8, centeredLeft), maxLeft);

      return {
        key: this.toLocalDateString(date),
        title,
        message,
        style: {
          top: `${top}px`,
          left: `${left}px`,
        },
      };
    },
    showTooltip(date, target) {
      const payload = this.buildTooltipPayload(date, target);
      if (!payload) return;

      this.activeTooltip = payload;
      this.tooltipStyle = payload.style || { top: "0px", left: "0px" };
    },
    hideTooltip() {
      this.activeTooltip = null;
    },
    handleDayMouseEnter(day, event) {
      if (!day || !this.supportsHover()) return;
      if (!this.isDateDisabled(day.date) && !this.isApprovedBookingRequestDate(day.date)) return;
      this.showTooltip(day.date, event.currentTarget);
    },
    handleDayMouseLeave(day) {
      if (!day || !this.supportsHover()) return;
      this.hideTooltip();
    },
    handleDayClick(day, event) {
      if (!day) return;

      const isApprovedDate = this.isApprovedBookingRequestDate(day.date);

      if (!this.isDateDisabled(day.date)) {
        this.hideTooltip();
        this.selectDate(day.date);
        if (isApprovedDate) {
          this.showTooltip(day.date, event.currentTarget);
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.showTooltip(day.date, event.currentTarget);
    },
    supportsHover() {
      return typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(hover: hover)").matches;
    },
    handleDocumentClick(event) {
      if (!this.activeTooltip) return;
      if (this.$el && this.$el.contains(event.target)) return;
      this.hideTooltip();
    },
  },
  watch: {
    selectedDate(newVal) {
      if (newVal && newVal.startDate && newVal.endDate) {
        this.internalSelectedDate = {
          startDate: newVal.startDate instanceof Date ? newVal.startDate : new Date(newVal.startDate),
          endDate: newVal.endDate instanceof Date ? newVal.endDate : new Date(newVal.endDate)
        };
      } else {
        this.internalSelectedDate = newVal;
      }
      const anchorDate = this.getSelectedAnchorDate();
      if (anchorDate) {
        this.setCurrentMonth(anchorDate);
      }
    },
    focusMonth(newDate) {
      if (newDate instanceof Date && !isNaN(newDate)) {
        this.currentMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
        console.log("focusMonth triggered:", this.currentMonth);
      }
    },
    checkInDate(newDate) {
      console.log("checkInDate watcher triggered with newDate:", newDate); // Debugging
      const hasSelectedDate = !!this.getSelectedAnchorDate();
      if (newDate && this.$refs.datePicker && !hasSelectedDate) {
        this.setCurrentMonth(newDate);
      }
    },
    currentMonth(newData) {
      if (newData) {
        this.getRoomOccupancy();
      }
    },
    roomId(newValue) {
      this.roomIdState = newValue;
      if (newValue) {
        this.getRoomOccupancy();
      }
    },
    roomTypeId(newValue) {
      this.roomTypeIdState = newValue;
      if (newValue) {
        this.getRoomOccupancy();
      }
    },
    propertyId(newValue) {
      this.propertyIdState = newValue;
    },
    availabilityWindowDays() {
      this.clampCurrentMonth();
    },
    apiAvailabilityWindowDays() {
      this.clampCurrentMonth();
    },
  },
  mounted() {
    console.log("selectedDatexxxxxxx", this.selectedDate);
    console.log("selectedDatexxxxxxx", this.roomId);
    if (this.roomId || this.roomTypeId) {
      this.getRoomOccupancy();
    }

    const selectedAnchorDate = this.getSelectedAnchorDate();
    if (selectedAnchorDate) {
      this.setCurrentMonth(selectedAnchorDate);
    } else if (this.checkInDate) {
      this.setCurrentMonth(this.checkInDate);
    }

    this.clampCurrentMonth();
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  }
};
</script>

<style scoped>
/* Transition Styles */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* High z-index to overlay other elements */
.z-100 {
  z-index: 1100;
}

select {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
  color: #374151;
}

select:focus {
  outline: none;
  border-color: #60a5fa;
}

/* Styling for responsiveness */
@media (max-width: 640px) {
  .max-w-full {
    width: 100%;
  }
}

@media (min-width: 640px) {
  .sm\:max-w-sm {
    max-width: 24rem;
  }
}

@media (min-width: 768px) {
  .md\:max-w-md {
    max-width: 28rem;
  }
}

@media (min-width: 1024px) {
  .lg\:max-w-lg {
    max-width: 32rem;
  }
}

/* Styling for past dates */
.text-gray-500 {
  color: #9ca3af;
}

.cursor-not-allowed {
  cursor: not-allowed;
}
</style>
